/**
 * Persistent Notification Component
 * 
 * Fixed-position notification area for critical alerts that must be manually dismissed.
 * Displays at the top-center of the screen with higher z-index than toasts.
 */

import { useState } from "react";
import { X, AlertTriangle, Skull, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PersistentNotificationData {
  id: string;
  severity: "warning" | "error";
  title: string;
  message: string;
  icon?: "warning" | "skull" | "shield";
}

interface PersistentNotificationProps {
  notification: PersistentNotificationData;
  onDismiss: (id: string) => void;
}

const getIcon = (icon?: string) => {
  switch (icon) {
    case "skull":
      return Skull;
    case "shield":
      return ShieldAlert;
    default:
      return AlertTriangle;
  }
};

const getSeverityStyles = (severity: "warning" | "error") => {
  if (severity === "error") {
    return {
      bg: "bg-ask/20 border-ask",
      icon: "text-ask-foreground",
      text: "text-foreground",
    };
  }
  return {
    bg: "bg-orange-500/20 border-orange-500",
    icon: "text-orange-500",
    text: "text-foreground",
  };
};

export const PersistentNotification = ({ notification, onDismiss }: PersistentNotificationProps) => {
  const Icon = getIcon(notification.icon);
  const styles = getSeverityStyles(notification.severity);
  
  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border-2 shadow-lg backdrop-blur-sm",
        styles.bg
      )}
    >
      <div className={cn("flex-shrink-0 mt-0.5", styles.icon)}>
        <Icon className="h-5 w-5" />
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className={cn("text-sm font-semibold", styles.text)}>
          {notification.title}
        </h4>
        <p className={cn("text-xs mt-1", styles.text, "opacity-90")}>
          {notification.message}
        </p>
      </div>
      
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDismiss(notification.id)}
        className="h-6 w-6 flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Container for managing multiple persistent notifications
interface PersistentNotificationContainerProps {
  notifications: PersistentNotificationData[];
  onDismiss: (id: string) => void;
}

export const PersistentNotificationContainer = ({
  notifications,
  onDismiss,
}: PersistentNotificationContainerProps) => {
  if (notifications.length === 0) return null;
  
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md px-4 space-y-2">
      {notifications.map((notification) => (
        <PersistentNotification
          key={notification.id}
          notification={notification}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

// Hook for managing persistent notifications
export const usePersistentNotifications = () => {
  const [notifications, setNotifications] = useState<PersistentNotificationData[]>([]);
  
  const addNotification = (notification: Omit<PersistentNotificationData, "id">) => {
    const id = `persistent-${Date.now()}-${Math.random()}`;
    setNotifications((prev) => [...prev, { ...notification, id }]);
  };
  
  const dismissNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };
  
  const dismissAll = () => {
    setNotifications([]);
  };
  
  return {
    notifications,
    addNotification,
    dismissNotification,
    dismissAll,
  };
};

